import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { noop } from 'lodash';

import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { getScrollbarWidth } from 'Utilities/helpers';
import useIntersectionObserver from 'Utilities/hooks/useIntersectionObserver';
import { useScrollData } from 'Utilities/hooks/useScrollData';

import styles from './StickyHeader.module.scss';

const intersectDirections = {
	DOWN: 'down',
	UP: 'up',
};

const StickyHeader = ({ children, noVehiclesFound, onChange }) => {
	const { isScrollingUp } = useScrollData();

	const stickyHeaderTopAnchor = useRef();
	const stickyHeaderBottomAnchor = useRef();
	const [isSticky, setIsSticky] = useState(false);
	const [isHidden, setIsHidden] = useState(false);
	const [isScrolledPastBottom, setIsScrolledPastBottom] = useState(false);
	const [intersectDirection, setIntersectDirection] = useState(intersectDirections.DOWN);
	const {
		minWidth: { breakpointContent },
	} = useBreakpoint();

	const onTopIntersect = useCallback(
		([{ isIntersecting }]) => {
			setIntersectDirection(isIntersecting ? intersectDirections.DOWN : intersectDirection);
		},
		[intersectDirection],
	);

	const onBottomIntersect = useCallback(([{ isIntersecting }]) => {
		setIntersectDirection(intersectDirections.UP);
		setIsScrolledPastBottom(!isIntersecting);
	}, []);

	useIntersectionObserver({
		onIntersect: onTopIntersect,
		target: stickyHeaderTopAnchor,
	});

	useIntersectionObserver({
		onIntersect: onBottomIntersect,
		target: stickyHeaderBottomAnchor,
	});

	useEffect(() => {
		const scrollbarWidth = getScrollbarWidth();
		document.documentElement.style.setProperty('--scrollBarWidth', `${scrollbarWidth}px`);
	}, []);

	useEffect(() => {
		const { DOWN, UP } = intersectDirections;
		const isScrollingDownPastBottom = isScrolledPastBottom && !isScrollingUp;
		const shouldBeSticky = !breakpointContent && (isScrolledPastBottom || intersectDirection === UP);
		const shouldBeHidden = !breakpointContent && (isScrollingDownPastBottom || intersectDirection === DOWN);

		setIsSticky(shouldBeSticky);
		setIsHidden(shouldBeHidden);
		onChange({ isHidden: shouldBeHidden, isSticky: shouldBeSticky });
	}, [breakpointContent, isScrolledPastBottom, intersectDirection, onChange, isScrollingUp]);

	return (
		<>
			<div ref={stickyHeaderTopAnchor} />
			<header
				className={cx(styles.stickyHeaderToolbarContainer, {
					[styles.isSticky]: isSticky,
					[styles.isHidden]: isHidden,
					[styles.isDisabled]: !breakpointContent && noVehiclesFound,
				})}
			>
				<div className={styles.fakeToolbar} />
				{children}
			</header>
			<div ref={stickyHeaderBottomAnchor} />
		</>
	);
};

StickyHeader.defaultProps = {
	noVehiclesFound: undefined,
	onChange: noop,
};

StickyHeader.propTypes = {
	children: PropTypes.node.isRequired,
	noVehiclesFound: PropTypes.bool,
	onChange: PropTypes.func,
};

export default StickyHeader;
